/**
 * CastingForm — handles client-side validation, file uploads, and submission
 * for the mdlondon casting/consultation application form.
 *
 * Submits to an external Cloud Function endpoint via fetch with FormData.
 */

class CastingForm {
  static MAX_FILES = 5;
  static MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  static ALLOWED_TYPES = ['image/jpeg', 'image/png'];

  constructor(container) {
    this.container = container;
    this.form = container.querySelector('form');
    if (!this.form) return;

    this.endpoint = this.form.dataset.endpoint || this.form.action;
    this.files = [];

    this.errorSummary = container.querySelector('[id$="-error-summary"]');
    this.errorSummaryText = container.querySelector('[id$="-error-summary-text"]');
    this.successMessage = container.querySelector('[id$="-success"]');
    this.submitButton = this.form.querySelector('button[type="submit"]');
    this.buttonText = this.submitButton.querySelector('.casting-form__button-text');
    this.buttonSpinner = this.submitButton.querySelector('.casting-form__button-spinner');

    this.uploadZone = this.form.querySelector('[id$="-upload-zone"]');
    this.fileInput = this.form.querySelector('input[type="file"]');
    this.thumbnailContainer = this.form.querySelector('[id$="-thumbnails"]');
    this.photoCount = this.form.querySelector('[id$="-photos-count"]');

    this.bindEvents();
  }

  bindEvents() {
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));

    // File upload - click / keyboard on zone
    this.uploadZone.addEventListener('click', () => this.fileInput.click());
    this.uploadZone.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.fileInput.click();
      }
    });

    this.fileInput.addEventListener('change', () => {
      this.addFiles(this.fileInput.files);
      this.fileInput.value = '';
    });

    // Drag and drop
    this.uploadZone.addEventListener('dragenter', (e) => this.handleDragEnter(e));
    this.uploadZone.addEventListener('dragover', (e) => this.handleDragOver(e));
    this.uploadZone.addEventListener('dragleave', (e) => this.handleDragLeave(e));
    this.uploadZone.addEventListener('drop', (e) => this.handleDrop(e));

    // Clear errors on input
    this.form.querySelectorAll('input, textarea, select').forEach((input) => {
      const event = input.type === 'radio' || input.type === 'checkbox' ? 'change' : 'input';
      input.addEventListener(event, () => {
        const fieldId = input.name;
        this.clearError(fieldId);
      });
    });
  }

  handleDragEnter(e) {
    e.preventDefault();
    e.stopPropagation();
    this.uploadZone.classList.add('casting-form__upload-zone--dragover');
  }

  handleDragOver(e) {
    e.preventDefault();
    e.stopPropagation();
  }

  handleDragLeave(e) {
    e.preventDefault();
    e.stopPropagation();
    this.uploadZone.classList.remove('casting-form__upload-zone--dragover');
  }

  handleDrop(e) {
    e.preventDefault();
    e.stopPropagation();
    this.uploadZone.classList.remove('casting-form__upload-zone--dragover');
    if (e.dataTransfer && e.dataTransfer.files) {
      this.addFiles(e.dataTransfer.files);
    }
  }

  addFiles(fileList) {
    const newFiles = Array.from(fileList);
    let errors = [];

    for (const file of newFiles) {
      if (this.files.length >= CastingForm.MAX_FILES) {
        errors.push(`Maximum ${CastingForm.MAX_FILES} photos allowed.`);
        break;
      }
      if (!CastingForm.ALLOWED_TYPES.includes(file.type)) {
        errors.push(`"${file.name}" is not a supported format. Please use JPG or PNG.`);
        continue;
      }
      if (file.size > CastingForm.MAX_FILE_SIZE) {
        errors.push(`"${file.name}" exceeds the 10MB size limit.`);
        continue;
      }
      this.files.push(file);
    }

    if (errors.length) {
      this.showError('photos', errors.join(' '));
    } else {
      this.clearError('photos');
    }

    this.renderThumbnails();
    this.updatePhotoCount();
  }

  removeFile(index) {
    this.files.splice(index, 1);
    this.renderThumbnails();
    this.updatePhotoCount();
    this.clearError('photos');
  }

  renderThumbnails() {
    this.thumbnailContainer.innerHTML = '';

    this.files.forEach((file, index) => {
      const thumb = document.createElement('div');
      thumb.className = 'casting-form__thumbnail';
      thumb.setAttribute('role', 'listitem');

      const img = document.createElement('img');
      img.className = 'casting-form__thumbnail-img';
      img.src = URL.createObjectURL(file);
      img.alt = `Uploaded photo ${index + 1}: ${file.name}`;
      img.addEventListener('load', () => URL.revokeObjectURL(img.src));

      const info = document.createElement('span');
      info.className = 'casting-form__thumbnail-info';
      info.textContent = this.formatFileSize(file.size);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'casting-form__thumbnail-remove';
      removeBtn.setAttribute('aria-label', `Remove photo ${index + 1}`);
      removeBtn.innerHTML = '&times;';
      removeBtn.addEventListener('click', () => this.removeFile(index));

      thumb.append(img, info, removeBtn);
      this.thumbnailContainer.appendChild(thumb);
    });
  }

  updatePhotoCount() {
    this.photoCount.textContent = `${this.files.length} of ${CastingForm.MAX_FILES} photos uploaded`;
  }

  formatFileSize(bytes) {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }

  async handleSubmit(e) {
    e.preventDefault();
    this.clearAllErrors();

    // Honeypot check
    const honeypot = this.form.querySelector('input[name="website"]');
    if (honeypot && honeypot.value) return;

    if (!this.validate()) return;

    this.setLoading(true);

    try {
      const formData = new FormData();

      // Text fields
      const fields = [
        'full_name', 'age_range', 'region', 'email', 'mobile',
        'instagram', 'hair_change', 'hair_concern', 'hair_story',
        'social_consent', 'email_optin', 'sms_optin'
      ];

      for (const name of fields) {
        const el = this.form.querySelector(`[name="${name}"]`);
        if (!el) continue;

        if (el.type === 'radio') {
          const checked = this.form.querySelector(`[name="${name}"]:checked`);
          if (checked) formData.append(name, checked.value);
        } else if (el.type === 'checkbox') {
          formData.append(name, el.checked ? 'true' : 'false');
        } else {
          formData.append(name, el.value.trim());
        }
      }

      // Files
      for (const file of this.files) {
        formData.append('photos', file);
      }

      const response = await fetch(this.endpoint, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        this.form.hidden = true;
        this.errorSummary.hidden = true;
        this.successMessage.hidden = false;
        this.successMessage.focus();
        this.container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        let data;
        try {
          data = await response.json();
        } catch {
          data = null;
        }

        if (data && data.field) {
          this.showError(data.field, data.error);
          this.showSummaryError('Please correct the error below and try again.');
        } else if (data && data.error) {
          this.showSummaryError(data.error);
        } else {
          this.showSummaryError('Something went wrong. Please try again.');
        }
      }
    } catch {
      this.showSummaryError('A network error occurred. Please check your connection and try again.');
    } finally {
      this.setLoading(false);
    }
  }

  validate() {
    let valid = true;
    const firstInvalid = [];

    // Required text/email/tel/select fields
    const requiredFields = [
      { name: 'full_name', label: 'Full Name' },
      { name: 'region', label: 'Region' },
      { name: 'email', label: 'Email Address' },
      { name: 'mobile', label: 'Mobile Number' },
      { name: 'hair_change', label: 'This question' },
      { name: 'hair_concern', label: 'This question' },
      { name: 'hair_story', label: 'This question' },
    ];

    for (const { name, label } of requiredFields) {
      const el = this.form.querySelector(`[name="${name}"]`);
      if (el && !el.value.trim()) {
        this.showError(name, `${label} is required.`);
        firstInvalid.push(el);
        valid = false;
      }
    }

    // Email format
    const emailEl = this.form.querySelector('[name="email"]');
    if (emailEl && emailEl.value.trim()) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(emailEl.value.trim())) {
        this.showError('email', 'Please enter a valid email address.');
        if (!firstInvalid.includes(emailEl)) firstInvalid.push(emailEl);
        valid = false;
      }
    }

    // Required radio groups
    const requiredRadios = [
      { name: 'age_range', label: 'Age Range' },
      { name: 'social_consent', label: 'Social Media Consent' },
    ];

    for (const { name, label } of requiredRadios) {
      const checked = this.form.querySelector(`[name="${name}"]:checked`);
      if (!checked) {
        this.showError(name, `${label} is required.`);
        const first = this.form.querySelector(`[name="${name}"]`);
        if (first) firstInvalid.push(first);
        valid = false;
      }
    }

    // Files
    if (this.files.length === 0) {
      this.showError('photos', 'Please upload at least one photo.');
      firstInvalid.push(this.uploadZone);
      valid = false;
    }

    if (!valid && firstInvalid.length) {
      this.showSummaryError('Please correct the errors below and try again.');
      const target = firstInvalid[0];
      if (target.focus) target.focus();
    }

    return valid;
  }

  showError(fieldName, message) {
    // Find the error span by looking for the one whose ID ends with the field name pattern
    const errorSpans = this.form.querySelectorAll('.casting-form__error');
    for (const span of errorSpans) {
      if (span.id && span.id.includes(fieldName.replace(/_/g, '-'))) {
        span.textContent = message;
        break;
      }
    }

    // Set aria-invalid on the input
    const input = this.form.querySelector(`[name="${fieldName}"]`);
    if (input) {
      if (input.type === 'radio') {
        // For radios, mark the group container
        const group = input.closest('.casting-form__radio-group');
        if (group) group.setAttribute('aria-invalid', 'true');
      } else {
        input.setAttribute('aria-invalid', 'true');
      }
    }

    // Special case for photos - mark upload zone
    if (fieldName === 'photos') {
      this.uploadZone.setAttribute('aria-invalid', 'true');
    }
  }

  clearError(fieldName) {
    const errorSpans = this.form.querySelectorAll('.casting-form__error');
    for (const span of errorSpans) {
      if (span.id && span.id.includes(fieldName.replace(/_/g, '-'))) {
        span.textContent = '';
        break;
      }
    }

    const input = this.form.querySelector(`[name="${fieldName}"]`);
    if (input) {
      if (input.type === 'radio') {
        const group = input.closest('.casting-form__radio-group');
        if (group) group.removeAttribute('aria-invalid');
      } else {
        input.removeAttribute('aria-invalid');
      }
    }

    if (fieldName === 'photos') {
      this.uploadZone.removeAttribute('aria-invalid');
    }
  }

  clearAllErrors() {
    this.form.querySelectorAll('.casting-form__error').forEach((el) => {
      el.textContent = '';
    });
    this.form.querySelectorAll('[aria-invalid]').forEach((el) => {
      el.removeAttribute('aria-invalid');
    });
    this.errorSummary.hidden = true;
    this.errorSummaryText.textContent = '';
  }

  showSummaryError(message) {
    this.errorSummaryText.textContent = message;
    this.errorSummary.hidden = false;
    this.errorSummary.focus();
  }

  setLoading(loading) {
    this.submitButton.disabled = loading;
    this.buttonText.hidden = loading;
    this.buttonSpinner.hidden = !loading;

    if (loading) {
      this.submitButton.setAttribute('aria-busy', 'true');
    } else {
      this.submitButton.removeAttribute('aria-busy');
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-casting-form]').forEach((el) => new CastingForm(el));
});
